import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F9] px-4">
        <div className="w-full max-w-md bg-white rounded-xl border border-[#BFC6CC] p-8 text-center">
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-3">Lien invalide</h2>
          <p className="text-[#687175] mb-6">
            Ce lien de réinitialisation est invalide ou a expiré. Veuillez en demander un nouveau.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 bg-[#397DFF] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#2563EB] transition-colors"
            data-testid="request-new-link"
          >
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonErr) {
        data = { detail: 'Erreur serveur' };
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la réinitialisation');
      }

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F9] px-4">
        <div className="w-full max-w-md bg-white rounded-xl border border-[#BFC6CC] p-8 text-center">
          <div className="w-16 h-16 bg-[#10D183]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#10D183]" />
          </div>
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-3" data-testid="reset-password-success-title">Mot de passe modifié</h2>
          <p className="text-[#687175] mb-6">
            Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion...
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#397DFF] font-medium hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F6F9] px-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-[#BFC6CC] p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#397DFF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#397DFF]" />
          </div>
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-2" data-testid="reset-password-title">Nouveau mot de passe</h2>
          <p className="text-[#687175] text-sm">
            Choisissez un nouveau mot de passe pour votre compte.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="reset-password-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-1">Nouveau mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] focus:border-[#397DFF] transition-all text-sm pr-10"
                placeholder="6 caractères minimum"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#687175] hover:text-[#191D1E]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-1">Confirmer le mot de passe</label>
            <input
              type={showPassword ? 'text' : 'password'}
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] focus:border-[#397DFF] transition-all text-sm"
              placeholder="Confirmez le mot de passe"
              data-testid="reset-password-confirm-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#397DFF] hover:bg-[#2563EB] text-white font-medium py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50"
            data-testid="reset-password-submit-button"
          >
            {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-[#687175] hover:text-[#397DFF] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
